import { useState, useRef, useEffect } from "react";
import { useAuth } from "../../../../content/auth/AuthContext";
import { useAppContext } from "../../../../lib/AppContext";
import { uploadToCloudinary } from "../../lib/uploadToCloudinary";
import style from "../blogs/Blogs.module.scss";
import listStyle from "./SampleLibraries.module.scss";
import {
  FiTrash2,
  FiEdit,
  FiImage,
  FiLink,
  FiExternalLink,
  FiArrowUp,
  FiArrowDown,
  FiCheckCircle,
  FiXCircle,
} from "react-icons/fi";

const DEFAULT_ROLE = "Sample Editing, Processing & Preparation for Virtual Instruments";

const EMPTY_FORM = { brand: "", name: "", role: DEFAULT_ROLE, link: "" };

const unique = (values) => [...new Set(values.filter(Boolean))];

const SampleLibrariesAdminPage = () => {
  const { adminFetch } = useAuth();
  const { sampleLibraries, loading: isGlobalLoading, refetchAllData } = useAppContext();

  // Local copy so reordering feels instant; resynced whenever the server data changes
  const [libraries, setLibraries] = useState(sampleLibraries);
  useEffect(() => setLibraries(sampleLibraries), [sampleLibraries]);

  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [removeImage, setRemoveImage] = useState(false);
  const [editing, setEditing] = useState(null); // the library being edited, or null when creating

  const [isSaving, setIsSaving] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const formRef = useRef(null);

  const brandOptions = unique(libraries.map((l) => l.brand));
  const roleOptions = unique([DEFAULT_ROLE, ...libraries.map((l) => l.role)]);

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setRemoveImage(false);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const clearForm = () => {
    setForm(EMPTY_FORM);
    setImageFile(null);
    setImagePreview("");
    setRemoveImage(false);
    setEditing(null);
    formRef.current?.reset();
  };

  const startEditing = (library) => {
    formRef.current?.reset();
    setEditing(library);
    setForm({
      brand: library.brand,
      name: library.name,
      role: library.role,
      link: library.link || "",
    });
    setImageFile(null);
    setImagePreview(library.imageUrl || "");
    setRemoveImage(false);
    setMessage({ type: "", text: "" });
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;
    setIsSaving(true);

    try {
      let imageUrl;
      if (imageFile) {
        setMessage({ type: "info", text: "Uploading image..." });
        imageUrl = await uploadToCloudinary(imageFile, "libraryImage", adminFetch);
      }

      setMessage({ type: "info", text: "Saving library..." });
      const fields = { ...form, imageUrl, removeImage };
      if (editing) {
        await adminFetch(`/api/admin/sample-libraries/${editing.id}`, {
          method: "PUT",
          json: fields,
        });
      } else {
        await adminFetch("/api/admin/sample-libraries", { method: "POST", json: fields });
      }

      setMessage({
        type: "success",
        text: editing ? "Library updated successfully!" : "Library added to the end of the list!",
      });
      clearForm();
      await refetchAllData();
    } catch (error) {
      setMessage({ type: "error", text: `Error: ${error.message}` });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (library) => {
    if (!window.confirm(`Delete "${library.brand} - ${library.name}"?`)) return;
    setMessage({ type: "info", text: "Deleting library..." });
    try {
      await adminFetch(`/api/admin/sample-libraries/${library.id}`, { method: "DELETE" });
      if (editing?.id === library.id) clearForm();
      setMessage({ type: "success", text: "Library deleted successfully." });
      await refetchAllData();
    } catch (error) {
      setMessage({ type: "error", text: `Failed to delete: ${error.message}` });
    }
  };

  const move = async (index, direction) => {
    const target = index + direction;
    if (isReordering || target < 0 || target >= libraries.length) return;

    const previous = libraries;
    const next = [...libraries];
    [next[index], next[target]] = [next[target], next[index]];
    setLibraries(next);
    setIsReordering(true);

    try {
      await adminFetch("/api/admin/sample-libraries", {
        method: "PUT",
        json: { order: next.map((l) => l.id) },
      });
      await refetchAllData();
    } catch (error) {
      setLibraries(previous);
      setMessage({ type: "error", text: `Failed to reorder: ${error.message}` });
    } finally {
      setIsReordering(false);
    }
  };

  return (
    <div className={style.pageContainer}>
      <header className={style.pageHeader}>
        <h1>Manage Sample Libraries</h1>
        <p>
          Virtual instruments and sample libraries shown on the Sound Design page. Use the
          arrows to change the order they appear in.
        </p>
      </header>

      <div className={style.mainContent}>
        <div className={style.formContainer}>
          <form ref={formRef} onSubmit={handleSubmit} className={style.uploadForm}>
            <h3>{editing ? `Edit "${editing.name}"` : "Add New Library"}</h3>

            <input
              type="text"
              name="brand"
              value={form.brand}
              onChange={handleFieldChange}
              placeholder="Brand (e.g., Orchestral Tools)"
              list="library-brands"
              maxLength={100}
              required
            />
            <datalist id="library-brands">
              {brandOptions.map((brand) => (
                <option key={brand} value={brand} />
              ))}
            </datalist>

            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleFieldChange}
              placeholder="Product name (e.g., Gearbox)"
              maxLength={200}
              required
            />

            <input
              type="text"
              name="role"
              value={form.role}
              onChange={handleFieldChange}
              placeholder="What you did (e.g., Sound Design & Preset Creation)"
              list="library-roles"
              maxLength={300}
              required
            />
            <datalist id="library-roles">
              {roleOptions.map((role) => (
                <option key={role} value={role} />
              ))}
            </datalist>

            <div className={style.fileInputGroup}>
              <label>
                <FiLink /> Product page link:
              </label>
              <input
                type="url"
                name="link"
                value={form.link}
                onChange={handleFieldChange}
                placeholder="https://..."
                maxLength={2000}
              />
            </div>

            <div className={style.fileInputGroup}>
              <label>
                <FiImage /> Cover image (optional):
                {editing?.imageUrl && " leave empty to keep current"}
              </label>
              <input type="file" accept="image/*" onChange={handleImageChange} />
            </div>
            {imagePreview && !removeImage && (
              <img
                src={imagePreview}
                alt="Cover preview"
                className={style.imagePreview}
                style={{ height: "120px", objectFit: "contain" }}
              />
            )}
            {editing?.imageUrl && !imageFile && (
              <label className={style.checkboxRow}>
                <input
                  type="checkbox"
                  checked={removeImage}
                  onChange={(e) => setRemoveImage(e.target.checked)}
                />
                Remove current image
              </label>
            )}

            <button type="submit" disabled={isSaving} className={style.submitButton}>
              {isSaving ? "Saving..." : editing ? "Save Changes" : "Add Library"}
            </button>
            {editing && (
              <button
                type="button"
                onClick={clearForm}
                disabled={isSaving}
                className={style.secondaryButton}
              >
                Cancel Editing
              </button>
            )}

            {message.text && (
              <div className={`${style.message} ${style[message.type]}`}>
                {message.type === "success" && <FiCheckCircle />}
                {message.type === "error" && <FiXCircle />}
                {message.text}
              </div>
            )}
          </form>
        </div>

        <div className={style.listContainer}>
          <h3>Display Order</h3>
          {isGlobalLoading ? (
            <p>Loading...</p>
          ) : libraries.length === 0 ? (
            <p className={listStyle.empty}>No libraries yet. Add the first one with the form.</p>
          ) : (
            <ol className={listStyle.list}>
              {libraries.map((library, index) => (
                <li
                  key={library.id}
                  className={`${listStyle.row} ${editing?.id === library.id ? listStyle.editing : ""}`}
                >
                  <span className={listStyle.number}>{index + 1}</span>
                  {library.imageUrl ? (
                    <img src={library.imageUrl} alt="" className={listStyle.thumb} />
                  ) : (
                    <span className={listStyle.thumbPlaceholder}>{library.brand[0]}</span>
                  )}
                  <div className={listStyle.info}>
                    <span className={listStyle.brand}>{library.brand}</span>
                    <strong>{library.name}</strong>
                    <span className={listStyle.role}>{library.role}</span>
                  </div>
                  <div className={listStyle.actions}>
                    <button
                      className={style.iconButton}
                      onClick={() => move(index, -1)}
                      disabled={index === 0 || isReordering}
                      title="Move up"
                    >
                      <FiArrowUp />
                    </button>
                    <button
                      className={style.iconButton}
                      onClick={() => move(index, 1)}
                      disabled={index === libraries.length - 1 || isReordering}
                      title="Move down"
                    >
                      <FiArrowDown />
                    </button>
                    {library.link && (
                      <a
                        href={library.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={style.iconButton}
                        title="Open product page"
                      >
                        <FiExternalLink />
                      </a>
                    )}
                    <button
                      className={style.iconButton}
                      onClick={() => startEditing(library)}
                      title="Edit"
                    >
                      <FiEdit />
                    </button>
                    <button
                      className={`${style.iconButton} ${style.deleteButton}`}
                      onClick={() => handleDelete(library)}
                      title="Delete"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
};

export default SampleLibrariesAdminPage;
